'use client';

import { useState } from 'react';

import { usePathname, useRouter } from 'next/navigation';

import LogoCloud from '@/components/ui/LogoCloud';
import { prices, products, subscriptions } from '@/db/schema';
import { getErrorRedirect } from '@/utils/helpers';
import { getStripe } from '@/utils/stripe/client';
import { checkoutWithStripe } from '@/utils/stripe/server';

import Button from '../Button/Button';
import cn from 'classnames';
import type { User } from 'next-auth';

type Subscription = typeof subscriptions.$inferSelect;
type Product = typeof products.$inferSelect;
type Price = typeof prices.$inferSelect;
interface ProductWithPrices extends Product {
  prices: Price[];
}
interface PriceWithProduct extends Price {
  products: Product | null;
}
interface SubscriptionWithProduct extends Subscription {
  prices: PriceWithProduct | null;
}

interface Props {
  user: User | null | undefined;
  products: ProductWithPrices[];
  subscription: SubscriptionWithProduct | null;
}

type BillingInterval = 'lifetime' | 'year' | 'month';

export default function Pricing({ user, products, subscription }: Props) {
  const intervals = Array.from(
    new Set(
      products.flatMap((product) =>
        product?.prices?.map((price) => price?.interval)
      )
    )
  );
  const router = useRouter();
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>('month');
  const [priceIdLoading, setPriceIdLoading] = useState<string>();
  const currentPath = usePathname();

  const handleStripeCheckout = async (price: Price) => {
    setPriceIdLoading(price.id);

    if (!user) {
      setPriceIdLoading(undefined);
      return router.push('/auth/signup');
    }

    // After successful payment, redirect to blog to access premium content
    const { errorRedirect, sessionId } = await checkoutWithStripe(
      price,
      '/blog'
    );

    if (errorRedirect) {
      setPriceIdLoading(undefined);
      return router.push(errorRedirect);
    }

    if (!sessionId) {
      setPriceIdLoading(undefined);
      return router.push(
        getErrorRedirect(
          currentPath,
          'An unknown error occurred.',
          'Please try again later or contact a system administrator.'
        )
      );
    }

    const stripe = await getStripe();
    (stripe as any)?.redirectToCheckout({ sessionId });

    setPriceIdLoading(undefined);
  };

  if (!products.length) {
    return (
      <section className='px-4 py-8 sm:py-24'>
        <div className='mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-24 lg:px-8'>
          <div className='sm:align-center sm:flex sm:flex-col'></div>
          <p className='text-4xl font-extrabold sm:text-center sm:text-6xl'>
            No subscription pricing plans found. Create them in your{' '}
            <a
              className='text-brand-accent dark:text-brand-dark-text-secondary underline'
              href='https://dashboard.stripe.com/products'
              rel='noopener noreferrer'
              target='_blank'>
              Stripe Dashboard
            </a>
            .
          </p>
        </div>
        <LogoCloud />
      </section>
    );
  } else {
    return (
      <section className='px-4 py-8 sm:py-24'>
        <div className='mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-24 lg:px-8'>
          <div className='sm:align-center sm:flex sm:flex-col'>
            <h2 className='text-2xl font-extrabold sm:text-center sm:text-4xl'>
              The Power of Biological Data
            </h2>
            <p className='m-auto mt-5 max-w-2xl text-xl sm:text-center sm:text-2xl'>
              Join our mission to support creators through our weekly newsletter
              project. Subscribe to unlock exclusive content and help fuel the
              creative economy.
            </p>
            <div className='border-primary/10 bg-muted relative mt-6 flex self-center rounded-lg border p-0.5 sm:mt-8'>
              {intervals.includes('month') && (
                <button
                  onClick={() => setBillingInterval('month')}
                  type='button'
                  className={`${
                    billingInterval === 'month'
                      ? 'border-primary/20 bg-background text-foreground relative w-1/2 shadow-sm'
                      : 'text-muted-foreground relative ml-0.5 w-1/2 border border-transparent'
                  } focus:ring-primary focus:ring-opacity-50 m-1 rounded-md py-2 text-sm font-medium whitespace-nowrap focus:z-10 focus:ring-2 focus:outline-none sm:w-auto sm:px-8`}>
                  Monthly billing
                </button>
              )}
              {intervals.includes('year') && (
                <button
                  onClick={() => setBillingInterval('year')}
                  type='button'
                  className={`${
                    billingInterval === 'year'
                      ? 'border-primary/20 bg-background text-foreground relative w-1/2 shadow-sm'
                      : 'text-muted-foreground relative ml-0.5 w-1/2 border border-transparent'
                  } focus:ring-primary focus:ring-opacity-50 m-1 rounded-md py-2 text-sm font-medium whitespace-nowrap focus:z-10 focus:ring-2 focus:outline-none sm:w-auto sm:px-8`}>
                  Yearly billing
                </button>
              )}
            </div>
          </div>
          <div className='mt-12 flex flex-wrap justify-center gap-6 space-y-4 sm:mt-16 sm:space-y-0 lg:mx-auto lg:max-w-4xl xl:mx-0 xl:max-w-none'>
            {products.map((product) => {
              const price = product?.prices?.find(
                (price) => price.interval === billingInterval
              );
              if (!price) return null;
              const priceString = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: price.currency!,
                minimumFractionDigits: 0
              }).format((price?.unitAmount || 0) / 100);
              return (
                <div
                  key={product.id}
                  className={cn(
                    'bg-card flex flex-col rounded-2xl border-4 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_8px_0_0_#066653] dark:hover:shadow-[0_8px_0_0_rgba(255,255,255,0.1)]',
                    {
                      'border-primary': subscription
                        ? product.name === subscription?.prices?.products?.name
                        : product.name === 'Freelancer',
                      'border-secondary': !(subscription
                        ? product.name === subscription?.prices?.products?.name
                        : product.name === 'Freelancer')
                    },
                    'max-w-xs flex-1 basis-1/3'
                  )}>
                  <div className='p-6'>
                    <h2 className='text-foreground text-2xl leading-6 font-semibold'>
                      {product.name}
                    </h2>
                    <p className='text-muted-foreground mt-4'>
                      {product.description}
                    </p>
                    <p className='mt-8'>
                      <span className='text-foreground text-5xl font-extrabold'>
                        {priceString}
                      </span>
                      <span className='text-muted-foreground text-base font-medium'>
                        /{billingInterval}
                      </span>
                    </p>
                    <Button
                      variant='primary'
                      type='button'
                      loading={priceIdLoading === price.id}
                      onClick={() => handleStripeCheckout(price)}
                      className='mt-8 block w-full !rounded-full'>
                      {subscription &&
                      subscription.prices?.products?.id === product.id
                        ? 'Manage'
                        : 'Subscribe'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <LogoCloud />
        </div>
      </section>
    );
  }
}
